import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ClipboardCheck, Bell, AlertCircle } from 'lucide-react'
import { getReviews, getNotifications, markNotificationRead } from '../../services/api'
import { getNotificationNavTarget, notificationLabel, TYPE_ICONS } from '../../constants/notificationTypes'
import type { Notification } from '../../types'
import ReviewCard from '../../components/reviews/ReviewCard'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import { useTimedOut } from '../../hooks/useTimedOut'
import MyTodos from './crm/MyTodos'
import EmptyState from '../../components/ui/EmptyState'
import CreateReviewModal from '../../components/reviews/CreateReviewModal'
import PageHeader from '../../components/ui/PageHeader'

type Tab = 'received' | 'sent' | 'todos' | 'notifications'

export default function ReviewDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('received')
  const [showCreate, setShowCreate] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: reviews, isLoading: reviewsLoading, isError: reviewsError } = useQuery({
    queryKey: ['reviews', activeTab],
    queryFn: () => getReviews(activeTab === 'received' || activeTab === 'sent' ? activeTab : 'received'),
    enabled: activeTab === 'received' || activeTab === 'sent',
  })

  const { data: notifications, isLoading: notificationsLoading, isError: notificationsError } = useQuery({
    queryKey: ['notifications'],
    queryFn: getNotifications,
    enabled: activeTab === 'notifications',
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification.notification_id)
    }
    // BUG-102: route per notification type via the shared helper — blindly
    // navigating to /document/{resource_id} dead-ended at "Document not found"
    // for connection / disclosure / cross-pollination ids. '/notifications'
    // means "no better target than this page" — we're already here, so stay.
    const target = getNotificationNavTarget(notification)
    if (target !== '/notifications') {
      navigate(target)
    }
  }

  const isLoading = activeTab === 'todos' ? false : activeTab === 'notifications' ? notificationsLoading : reviewsLoading
  const isError = activeTab === 'todos' ? false : activeTab === 'notifications' ? notificationsError : reviewsError
  // Bound the loading flag so a slow/racy hard load never leaves the tab stuck on
  // skeletons forever (Fig50 was BROKEN this way — the Received tab never resolved).
  // Once timed out we fall through to the real content / honest empty state; if the
  // query later settles with data, react-query re-renders and shows it.
  const loadTimedOut = useTimedOut(isLoading)
  const showSkeleton = isLoading && !loadTimedOut

  return (
    <div>
      <PageHeader
        title="Tasks & Reviews"
        description="Everything waiting on you — review requests you have sent or received, and your notifications — in one queue."
        helpKey="tasks"
        primaryAction={activeTab !== 'notifications' ? (
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800"
          >
            Request Review
          </button>
        ) : undefined}
        secondaryActions={activeTab === 'notifications' && notifications && notifications.some((n) => !n.read) ? (
          <button
            onClick={() => notifications.filter((n) => !n.read).forEach((n) => markReadMutation.mutate(n.notification_id))}
            className="text-sm text-iflow-600 hover:text-iflow-700 font-medium"
          >
            Mark all as read
          </button>
        ) : undefined}
      />

      <div className="flex gap-1 mb-6 bg-sky-500/10 rounded-lg p-1 w-fit">
        {(['received', 'sent', 'todos', 'notifications'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
              activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'received' ? 'Received' : tab === 'sent' ? 'Sent' : tab === 'todos' ? 'To-Dos' : 'Notifications'}
          </button>
        ))}
      </div>

      {isError && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>Failed to load {activeTab === 'notifications' ? 'notifications' : 'reviews'}.</span>
        </div>
      )}

      {activeTab === 'todos' ? (
        /* To-Dos — the Tasks & Reviews hub now folds in the personal to-do list */
        <MyTodos embedded />
      ) : showSkeleton ? (
        <LoadingSkeleton rows={4} type="list" />
      ) : activeTab === 'notifications' ? (
        /* Notifications tab */
        !notifications?.length ? (
          <EmptyState
            icon={Bell}
            title="No notifications"
            description="You're all caught up! Notifications will appear here when you receive them."
          />
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <button
                key={notification.notification_id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                  notification.read
                    ? 'light-card hover:bg-white/30'
                    : 'bg-iflow-50 border-iflow-200 hover:bg-iflow-100'
                }`}
              >
                <div className="mt-0.5">{TYPE_ICONS[notification.type] || <Bell size={16} />}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.read ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>
                    {notificationLabel(notification.type)}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(notification.created_at).toLocaleString()}</p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-iflow-500 mt-2 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        )
      ) : (
        /* Reviews tabs (received / sent) */
        !reviews?.length ? (
          <EmptyState
            icon={ClipboardCheck}
            title={`No ${activeTab} reviews`}
            description={activeTab === 'sent' ? 'Request a review on your documents to get started.' : 'No reviews awaiting your feedback.'}
          />
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <ReviewCard key={review.review_id} review={review} type={activeTab} />
            ))}
          </div>
        )
      )}

      {showCreate && <CreateReviewModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
